// src/routes/merchants/picker/MerchantField.tsx
import { ChevronDown } from 'lucide-react'
import { useRef, useState, type RefObject } from 'react'
import { Icon } from '../../../platform/icons/Icon'
import { MerchantAvatar } from '../../../platform/primitives/MerchantAvatar'
import { useHitTarget } from '../../../platform/responsive/hitTarget'
import { MerchantPickerDialog, type MerchantPickerContext } from './MerchantPickerDialog'
import type { PickExtras } from '../catalogue/catalogueModel'
import type { PickerMerchant } from './merchantPickerModel'
import './merchantPicker.css'

/**
 * Replaces the 500-option merchant `<select>`: a button that names the current merchant and opens the
 * picker, `CategoryField`'s shape. The accessible name carries the field and its value
 * ("Merchant: Costco"), as the `<select>` announced.
 *
 * **Naming a value.** The caller passes `current` — what it knows names the stored value (a
 * transaction's own embedded merchant, say). A merchant picked here is remembered for the field's
 * life, so an optimistic value is named before the server confirms it. An id neither names reads
 * "Unknown merchant" rather than blank or "None" — unless the caller is still finding out, and says so
 * with `unresolvedName` ("Loading merchant…"), so a slow read is not reported as a missing merchant.
 *
 * **Disabled is `aria-disabled`**, so the field stays focusable and its reason (`describedBy`) is read.
 *
 * **Two skins.** `pill` (the default) is `CategoryField`'s filled chip with a text caret. `control` is
 * for a row of bordered `<select>`s (a rule's actions): the same border and fill as `.rule-control`, the
 * row's full width, and the drawn chevron `RuleSelect` uses, at the right edge. Both skins live in CSS.
 */
export function MerchantField({
  label,
  value,
  onChange,
  current = null,
  context,
  allowNone = false,
  offerCategory = false,
  disabled = false,
  describedBy,
  className,
  unresolvedName,
  skin = 'pill',
  openerRef,
}: {
  label: string
  value: string | null
  onChange: (id: string | null, merchant?: PickerMerchant, extras?: PickExtras) => void
  current?: PickerMerchant | null
  context?: MerchantPickerContext
  allowNone?: boolean
  /** The transaction has no category yet: an added brand may bring its suggested one (`extras`). */
  offerCategory?: boolean
  disabled?: boolean
  describedBy?: string
  className?: string
  /** The words for a value neither `current` nor a pick names YET; absent, such a value is "Unknown merchant". */
  unresolvedName?: string
  skin?: 'pill' | 'control'
  /** The field's button, for a caller that must move focus to it (a suggestion beside it closing). */
  openerRef?: RefObject<HTMLButtonElement | null>
}) {
  const [open, setOpen] = useState(false)
  const [picked, setPicked] = useState<ReadonlyMap<string, PickerMerchant>>(() => new Map())
  const ownOpener = useRef<HTMLButtonElement>(null)
  const opener = openerRef ?? ownOpener
  const minHeight = useHitTarget('page')
  const known = value === null ? undefined : (picked.get(value) ?? (current?.id === value ? current : undefined))
  const name = value === null ? 'No merchant' : (known?.name ?? unresolvedName ?? 'Unknown merchant')
  const shown = value === null ? 'Choose a merchant' : name
  return (
    <>
      <button
        ref={opener}
        type="button"
        className={['merchant-field', skin === 'control' ? 'merchant-field--control' : '', className].filter(Boolean).join(' ')}
        style={{ minHeight }}
        aria-haspopup="dialog"
        aria-label={`${label}: ${name}`}
        aria-disabled={disabled || undefined}
        aria-describedby={describedBy}
        onClick={() => !disabled && setOpen(true)}
      >
        {known !== undefined && (
          <span aria-hidden="true" className="merchant-browser__avatar">
            <MerchantAvatar merchant={known.identity} />
          </span>
        )}
        <span className="merchant-field__name">{shown}</span>
        {skin === 'control' ? (
          <span className="merchant-field__chevron" aria-hidden="true">
            <Icon glyph={ChevronDown} size={14} />
          </span>
        ) : (
          <span className="merchant-field__caret" aria-hidden="true">
            ▾
          </span>
        )}
      </button>
      <MerchantPickerDialog
        open={open}
        onClose={() => setOpen(false)}
        value={value}
        onPick={(id, merchant, extras) => {
          if (merchant !== undefined) setPicked((prev) => new Map(prev).set(merchant.id, merchant))
          if (extras === undefined) onChange(id, merchant)
          else onChange(id, merchant, extras)
        }}
        context={context}
        allowNone={allowNone}
        offerCategory={offerCategory}
        restoreFocusTo={opener}
      />
    </>
  )
}
